import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Form, Formik } from 'formik';
import Yup from '../../utils/yup';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

import { useLoader } from '../../provider/LoaderProvider';
import FormFields from './FormFields';
import { DeleteDialogUI, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import useUsers from '../../context/UsersContext';
import dayjs from 'dayjs';
import dailyReportService from '../../service/dailyReport.service';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';
import DailyReportTabs from './DailyReportTabs';
import useParam from '../../context/ParamContext';
import useMenus from '../../context/MenuContext';

export const DailyReportPage = ({ data = {}, handleClose, setTaskChanged }) => {
  const { menus, getMenus, getMenuSubMenuId } = useMenus();
  const { getUser, user } = useUsers();
  const { getParam, params } = useParam();
  const { showLoader, hideLoader } = useLoader();
  const [initValues, setInitValues] = useState({});
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    project: [],
    resourceOne: [],
    brigades: [],
  });
  const [autoCompleteData, setAutoCompleteData] = useState({});
  const [r1SubmenuId, setR1SubmenuId] = useState(null);
  const [viewField, setViewField] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const def = {
    projectId: null,
    date: dayjs(),
    startTime: dayjs('07:00', 'HH:mm'),
    endTime: dayjs('16:00', 'HH:mm'),
    dailyReportUser: [],
    attachment: [],
    description: '',
    icon: [],
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    setInitValues(def);
    getParam();
    getMenus();
  }, []);

  useEffect(() => {
    if (r1SubmenuId) {
      initDefaultValues();
    }
  }, [r1SubmenuId]);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('resourceOne');
    setR1SubmenuId(submenuId);
  }, [menus]);

  useEffect(() => {
    if (Object.keys(data).length > 0 && autocompleteOptions.resourceOne.length > 0) {
      const formatted = {
        ...data,
        date: dayjs(data.startDate),
        startTime: dayjs(data.startDate),
        endTime: dayjs(data.endDate),
        dailyReportUser:
          data.dailyReportUser?.map((u) => {
            return (
              autocompleteOptions.resourceOne.find((res) => res.value === u.resourceOneId) || {
                value: u.resourceOne.id,
                label: u.resourceOne.name,
              }
            );
          }) ?? [],
        attachment: data.dailyReportAttachment ?? [],
        icon: JSON.parse(data.icon),
      };

      setInitValues(formatted);
      setViewField(true);
    }
  }, [data, autocompleteOptions.resourceOne]);

  const validationSchema = Yup.object().shape({
    projectId: Yup.string().required(),
    date: Yup.string().required(),
    startTime: Yup.string().required(),
    endTime: Yup.string().required(),
    dailyReportUser: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
      )
      .min(1),
    description: Yup.string().nullable(),
  });

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceOneToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .statusToForm(r1SubmenuId)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          project: resp[0],
          resourceOne: resp[1],
          brigades: resp[2],
        };
        setAutoCompleteData(aData);
        const filteredResourceOne =
          Object.keys(data).length > 0 ? aData.resourceOne : aData.resourceOne.filter((r1) => r1.active);

        const acOptions = {
          project: generateAutocompleteItems(aData.project, 'projectName', 'projectId'),
          resourceOne: generateAutocompleteItems(filteredResourceOne, 'name', 'id'),
          brigades: generateAutocompleteItems(aData.brigades.statusItems, 'itemName', 'itemId'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const handleSubmit = (values, resetForm) => {
    if (Boolean(data?.id)) {
      updateObject(values);
    } else {
      handleInsertObject(values, resetForm);
    }
  };

  const handleInsertObject = (values, { resetForm }) => {
    showLoader();
    const insertObject = {
      ...values,
      createdBy: user.userId,
      startDate: dayjs(values.startDate).toISOString(),
      endDate: dayjs(values.endDate).toISOString(),
      dailyReportUser: values.dailyReportUser.map((u) => u.value),
      icon: JSON.stringify(values.icon),
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObject));

    if (insertObject.attachment) {
      for (const file of insertObject.attachment) {
        formData.append('files', file);
      }
    }

    dailyReportService
      .createData(formData)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        resetForm({ values: def });
      })
      .finally(() => {
        hideLoader();
      });
  };

  const updateObject = (values) => {
    const updData = {
      ...values,
      updatedBy: user.userId,
      startDate: dayjs(values.startDate).toISOString(),
      endDate: dayjs(values.endDate).toISOString(),
      dailyReportUser: values.dailyReportUser.map((u) => u.value),
      icon: JSON.stringify(values.icon),
    };

    updData.existingFiles = updData.attachment?.map((f) => f.name);

    const formData = new FormData();
    formData.append('data', JSON.stringify(updData));

    if (updData.attachment) {
      for (const file of updData.attachment) {
        if (file instanceof File) {
          formData.append('files', file);
        }
      }
    }

    dailyReportService.updateRow(formData).then(() => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      setViewField(true);
      setTaskChanged(true);
      handleClose();
    });
  };

  const handleRemoveElement = async () => {
    dailyReportService.deleteRow(data.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setTaskChanged(true);
      handleClose();
    });
  };

  return (
    <div
      className={`${!Object.keys(data).length > 0 && 'pt-20 fixed top-0 left-0 overflow-auto'} bg-gray-100 h-full w-full`}
    >
      <div
        className={`${!Object.keys(data).length > 0 && 'py-5 px-5 md:w-[650px] w-[400px]  mx-auto'} h-auto bg-white rounded-lg`}
      >
        {Object.keys(data).length > 0 && (
          <FormHeaderUI
            modClick={() => {
              setViewField(false);
            }}
            modDisabled={!viewField}
            deleteClick={() => {
              setShowDeleteDialog(true);
            }}
            deleteDisabled={!viewField}
          />
        )}
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values }) => (
            <Form>
              <FormFields
                autocompleteOptions={autocompleteOptions}
                user={user}
                viewField={viewField}
                autoCompleteData={autoCompleteData}
              />
              <DailyReportTabs viewField={viewField} />
              <FormButtonPanelUI
                disabled={
                  viewField || (params.DAILYREPORT_REQUIRED_ATTACHMENT === 'Y' && values.attachment?.length === 0)
                }
              />
            </Form>
          )}
        </Formik>
      </div>
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </div>
  );
};
